import { KeyboardEvent, MouseEvent, ReactElement, RefObject, useRef } from "react";
import { Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/_atoms/button";
import { Table, TableBody, TableHeader, TableRow } from "@/components/_atoms/table";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { MunicipalPropertyRegisterFieldsFragment } from "@/graphql/generated/graphql";
import { formatDateTime } from "@/lib/date-utils";

import { useThresholdRowVirtualizer } from "../hooks/use-threshold-row-virtualizer";

import { MunicipalPropertyTableCell } from "./municipal-property-table-cell";
import { MunicipalPropertyTableHead } from "./municipal-property-table-head";
import { MunicipalPropertyTableSpacerRow } from "./municipal-property-table-spacer-row";

const COLUMN_COUNT: number = 3;

interface MunicipalPropertyRegisterTableProps {
  registerList: readonly MunicipalPropertyRegisterFieldsFragment[];
  totalCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

export function MunicipalPropertyRegisterTable({
  registerList,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: MunicipalPropertyRegisterTableProps): ReactElement {
  const navigate: ReturnType<typeof useNavigate> = useNavigate();
  const { t, i18n } = useDomainTranslation("municipal-property");

  const scrollElementRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const { tableProps, headerRowProps, visibleRowList, topSpacerHeight, bottomSpacerHeight } =
    useThresholdRowVirtualizer({ rowList: registerList, totalCount, scrollElementRef });

  function handleRowKeyDown({ event, registerId }: { event: KeyboardEvent<HTMLTableRowElement>; registerId: string }): void {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      void navigate(registerId);
    }
  }

  return (
    <div ref={scrollElementRef} className="flex min-h-0 flex-1 flex-col overflow-auto">
      <Table
        containerClassName="overflow-visible"
        className="text-xs"
        {...tableProps}
      >
        <TableHeader className="[&_tr]:border-none">
          <TableRow {...headerRowProps}>
            <MunicipalPropertyTableHead>{t("register.heading")}</MunicipalPropertyTableHead>
            <MunicipalPropertyTableHead className="w-px text-right">
              {t("register.columns.year")}
            </MunicipalPropertyTableHead>
            <MunicipalPropertyTableHead className="w-px text-right">
              {t("register.columns.updatedAt")}
            </MunicipalPropertyTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <MunicipalPropertyTableSpacerRow height={topSpacerHeight} columnCount={COLUMN_COUNT} />
          {visibleRowList.map(({ row: register, rowProps }): ReactElement => (
            <TableRow
              key={register.id}
              {...rowProps}
              tabIndex={0}
              aria-label={t("register.detail.heading", { year: register.year })}
              className="cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
              onClick={(event: MouseEvent<HTMLTableRowElement>): void => {
                if (!(event.target instanceof Element) || !event.target.closest("a")) void navigate(register.id);
              }}
              onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>): void => handleRowKeyDown({ event, registerId: register.id })}
            >
              <MunicipalPropertyTableCell>
                <Link to={register.id} className="font-medium hover:underline">
                  {t("register.detail.heading", { year: register.year })}
                </Link>
              </MunicipalPropertyTableCell>
              <MunicipalPropertyTableCell className="text-right tabular-nums">
                {register.year}
              </MunicipalPropertyTableCell>
              <MunicipalPropertyTableCell className="whitespace-nowrap text-right tabular-nums text-muted-foreground">
                {formatDateTime(register.updatedAt, i18n?.language)}
              </MunicipalPropertyTableCell>
            </TableRow>
          ))}
          <MunicipalPropertyTableSpacerRow height={bottomSpacerHeight} columnCount={COLUMN_COUNT} />
        </TableBody>
      </Table>
      <div className="flex shrink-0 items-center justify-center gap-3 py-3">
        {hasNextPage ? (
          <Button variant="outline" size="xs" onClick={onLoadMore} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? (
              <Loader2 data-icon="inline-start" className="animate-spin" aria-hidden="true" />
            ) : null}
            {isFetchingNextPage ? t("catalog.loading") : t("catalog.loadMore")}
          </Button>
        ) : null}
        <span className="text-xs text-muted-foreground">
          {t("register.showing", {
            loaded: registerList.length,
            total: totalCount,
          })}
        </span>
      </div>
    </div>
  );
}
